import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable, catchError, map, of, shareReplay } from 'rxjs';
import { TranslocoService } from '@ngneat/transloco';

import { generateModuleMenuURL } from 'projects/lib-shared-common/src/public-api';
import { UserService } from 'projects/lib-shared-core/src/public-api';
import { SvcAppSettings } from 'projects/lib-shared-common/src/public-api';
import { ActionPlanStatusTyped } from '../enums/action-plan-status-type.enum';
import {
  SvcActionPlanList,
  SvcActionPlanPagedList,
  SvcActionPlanSearchListItem,
  SvcActionPlanSearchListResponse,
  SvcActionPlanSystemByApplication,
  SvcActionPlanPublishByReferenceRequest,
  SvcActionPlanPublishByReferenceResponse,
  SvcActionPlanModuleName,
} from '../interfaces/svc-actionplan-list.interface';
import { AppEnvironmentConfig } from 'projects/config/model/environment.config.model';
import { LegacyIntegrationService } from '../../../../integrations/legacy-integration.service';

@Injectable({
  providedIn: 'root',
})
export class SvcActionPlanService {
  public static lastOpenedComponentId: string | null = null;
  public activeComponentId: string;

  private readonly _apiActionPlan: string = this._appConfig.APIs.apiUrlActionPlan;
  private readonly _apiAdministration: string = this._appConfig.APIs.apiUrlAdministration;
  private _activeModules$?: Observable<SvcActionPlanModuleName[]>;

  constructor(
    private _appConfig: AppEnvironmentConfig,
    private _appSettings: SvcAppSettings,
    private _http: HttpClient,
    private _legacyService: LegacyIntegrationService,
    private _translocoService: TranslocoService,
    private _userService: UserService
  ) {
    this._legacyService.addLegacySupport();
  }

  public openDialogActionPlanLegacy(
    systemReferenceId: number,
    planId: number,
    isEdit: boolean,
    isDraft: boolean,
    additionalReferenceId = 1,
    paramPlanName?: string,
    appId?: string,
    additionalReferenceId2?: number
  ): void {
    const applicationId: string = appId ?? this._appSettings.applicationId;

    const url: string | Function = generateModuleMenuURL(
      'MESSAGE',
      location.origin,
      'openModal',
      `{
        "functionName": "OpenDynamicModal",
        "urlToOpen": "/actionplan/Plan/View",
        "modalData": {
          "flEdit": ${isEdit},
          "flDraft": ${isDraft},
          "planId": ${planId},
          "systemId": "${applicationId}",
          "systemAdditionalReferenceId": ${additionalReferenceId},
          "systemReferenceId": ${systemReferenceId},
          "systemAdditionalReferenceId2": ${additionalReferenceId2 ?? 'null'},
          "systemAdditional2ReferenceId": ${additionalReferenceId2 ?? 'null'},
          "hdnSystemAdditional2ReferenceId": ${additionalReferenceId2 ?? 'null'}
        },
        "modalId": "#registry-action-plan-modal",
        "modalTimeout": 0,
        "loadingText": "${this._translocoService.translate('Carregando')}",
        "paramPlanName": "${this._translocoService.translate(paramPlanName) ?? ''}"
      }`
    );

    if (typeof url === 'function') url();
  }

  private _getSearchListParams(
    applicationId: string,
    referenceValue: number,
    pageIndex: number,
    referenceTypeIds: number[],
    additionalReferenceIds: number[],
    includeDraft: boolean,
    pageSize: number,
    generalSearch: string
  ): HttpParams {
    let params = new HttpParams()
      .append('applicationId', applicationId)
      .append('referenceValue', referenceValue)
      .append('pageSize', pageSize)
      .append('pageIndex', pageIndex);

    if (includeDraft) params = params.append('includeDraft', true);

    const trimmedSearch = (generalSearch ?? '').trim();
    if (trimmedSearch) params = params.append('actionPlanUniqueId', trimmedSearch);

    referenceTypeIds.forEach((referenceTypeId, index) => {
      const additionalReferenceId = additionalReferenceIds[index];
      if (referenceTypeId === null || referenceTypeId === undefined) return;
      if (additionalReferenceId === null || additionalReferenceId === undefined) return;
      params = params.append('additionalReferenceId', additionalReferenceId).append('referenceTypeId', referenceTypeId);
    });

    return params;
  }

  private _mapActionsPlans(actionPlans: SvcActionPlanList[]): SvcActionPlanList[] {
    return actionPlans?.map((actionPlan: SvcActionPlanList) => {
      return {
        ...actionPlan,
        delayedDays: Math.abs(actionPlan?.delayedDays ?? 0),
      };
    });
  }

  private _mapSearchItemToActionPlan(item: SvcActionPlanSearchListItem): SvcActionPlanList {
    return {
      planId: item?.actionPlanUniqueId,
      actionPlanId: item?.actionPlanId,
      planName: item?.actionPlanName,
      statusId: item?.statusId,
      statusName: '',
      delayedDays: item?.ageInDays ?? 0,
      createUserId: '',
      senderName: item?.responsibleName ?? '',
      senderPicture: '',
      senderInitialsColor: '',
      originatorId: '',
      responsibleId: item?.responsibleUserId ?? '',
      classified: item?.confidential,
      allowed: item?.accessPermitted,
      previsionDate: item?.previsionDate,
    };
  }

  public getActionsPlans(
    referenceValue: number,
    applicationId: string,
    pageIndex: number,
    referenceTypeIds: number[],
    additionalReferenceIds: number[],
    includeDraft = false,
    pageSize = 10,
    generalSearch = ''
  ): Observable<SvcActionPlanPagedList> {
    const params: HttpParams = this._getSearchListParams(
      applicationId,
      referenceValue,
      pageIndex,
      referenceTypeIds,
      additionalReferenceIds,
      includeDraft,
      pageSize,
      generalSearch
    );

    return this._http
      .get<SvcActionPlanSearchListResponse>(`${this._apiActionPlan}/ActionPlan/search/list`, { params })
      .pipe(
        map((response: SvcActionPlanSearchListResponse) => {
          const data = this._mapActionsPlans((response?.data ?? []).map((item) => this._mapSearchItemToActionPlan(item))).sort(
            (first, second) => (second?.planId ?? 0) - (first?.planId ?? 0)
          );
          const completedTotalCount = (response?.data ?? []).filter(item => item.statusId === ActionPlanStatusTyped.completed).length;
          return { totalCount: response?.totalCount ?? 0, completedTotalCount, data } as SvcActionPlanPagedList;
        })
      );
  }

  private _getLinkedListParams(
    applicationId: string,
    referenceValue: number,
    pageIndex: number,
    additionalReferenceId: number,
    pageSize: number,
    generalSearch: string
  ): HttpParams {
    let params = new HttpParams()
      .append('applicationId', applicationId)
      .append('referenceId', referenceValue)
      .append('pageSize', pageSize)
      .append('pageIndex', pageIndex);

    const trimmedSearch = (generalSearch ?? '').trim();
    if (trimmedSearch) params = params.append('search', trimmedSearch);

    if (additionalReferenceId !== null && additionalReferenceId !== undefined) {
      params = params.append('additionalReferenceId', additionalReferenceId);
    }

    return params;
  }

  public getLinkedActionsPlans(
    referenceValue: number,
    applicationId: string,
    pageIndex: number,
    additionalReferenceId: number,
    pageSize = 10,
    generalSearch = ''
  ): Observable<SvcActionPlanPagedList> {
    const params: HttpParams = this._getLinkedListParams(applicationId, referenceValue, pageIndex, additionalReferenceId, pageSize, generalSearch);

    return this._http
      .get<SvcActionPlanPagedList>(`${this._apiActionPlan}/Analytics/actionplan/linked/paged-list`, { params })
      .pipe(
        map((response: SvcActionPlanPagedList) => {
          const data = this._mapActionsPlans(response?.data ?? []).sort((first, second) => (second?.planId ?? 0) - (first?.planId ?? 0));
          return { totalCount: response?.totalCount ?? 0, completedTotalCount: response?.completedTotalCount ?? 0, data } as SvcActionPlanPagedList;
        })
      );
  }

  public isModuleActive(applicationId: string | null | undefined): Observable<boolean> {
    const target = (applicationId ?? '').trim().toLowerCase();
    if (!target) return of(false);
    return this._getModuleNames().pipe(
      map((modules) => modules.some((module) => (module?.applicationId ?? '').trim().toLowerCase() === target && !!module?.isActive))
    );
  }

  private _getModuleNames(): Observable<SvcActionPlanModuleName[]> {
    if (!this._activeModules$) {
      this._activeModules$ = this._http.get<SvcActionPlanModuleName[]>(`${this._apiAdministration}/Modules/names`).pipe(
        map((modules) => modules ?? []),
        catchError(() => of([] as SvcActionPlanModuleName[])),
        shareReplay(1)
      );
    }
    return this._activeModules$;
  }

  public getCurrentSiteId(): number | undefined {
    return this._userService.user?.lastSiteId ?? this._userService.user?.originSiteId;
  }

  public getSystemByApplication(applicationId: string, siteId: number): Observable<SvcActionPlanSystemByApplication[]> {
    const params = new HttpParams().set('siteId', siteId).set('applicationId', applicationId);
    return this._http
      .get<SvcActionPlanSystemByApplication[]>(`${this._apiActionPlan}/System/by-application`, { params })
      .pipe(
        map((systems) => systems ?? []),
        catchError(() => of([]))
      );
  }

  public publishDraftsByReference(payload: SvcActionPlanPublishByReferenceRequest): Observable<SvcActionPlanPublishByReferenceResponse> {
    return this._http.post<SvcActionPlanPublishByReferenceResponse>(`${this._apiActionPlan}/ActionPlan/publish/by-reference`, payload);
  }

  public insertPlanFromDraftPlan(tempId: number, registerId: number, applicationId: string): Observable<unknown> {
    const params = new HttpParams()
      .set('siteId', this.getCurrentSiteId() ?? '')
      .set('applicationId', applicationId)
      .set('temp_id', tempId)
      .set('registerId', registerId);
    return this._http.get<unknown>(`${globalThis.location.origin}/helpers/actionPlan/InsertPlanFromDraftPlan`, { params });
  }
}
